import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ReplaySubject, Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { CubiqWorker } from "../../worker/CubiqWorker";

// Future use of this could be generatised to calling service /w smarter DI invocations
enum RequestType {
  Benchmark,
  HedgeOrder,
}

export abstract class AllBenchmarksSharedService<
  T extends CubiqTS.ConcreteEncodable,
  U extends CubiqTS.ConcreteEncodable,
> {
  // compelete and release all subjects after prime
  private responseSubject: ReplaySubject<U> | null = new ReplaySubject<U>(1);
  private requestSubject: Subject<RequestType> | null = new Subject<RequestType>();

  private get primeRequestedInError(): boolean {
    // prime is a oneshot
    return this.benchmarksRequested && this.hedgeOrdersRequested;
  }

  private get primeRequested(): boolean {
    return this.benchmarksRequested || this.hedgeOrdersRequested;
  }

  private benchmarksRequested: boolean = false;
  private hedgeOrdersRequested: boolean = false;

  private benchmarksPrimed: boolean = false;
  private hedgeOrdersPrimed: boolean = false;

  constructor(
    protected cubiqWorker: CubiqWorker,
    // not injectable; pass T 's constructor from subclass
    type: new () => T,
  ) {
    const responseSubject = this.responseSubject;
    this.requestSubject.subscribe((requestType) => {
      if (this.primeRequestedInError) {
        logger.out.error(
          `${this.constructor.name} a single prime per RequestType at contruct time is supported`,
        );
        return;
      } else if (this.primeRequested) {
        this.updateRequested(requestType);
        return;
      }

      this.updateRequested(requestType);

      let recentFixingsRequest = this.createRequest(type);
      this.cubiqWorker
        .postServiceRequest(recentFixingsRequest)
        .then((response) => responseSubject.next(response));
    });
  }

  private updateRequested(requestType): void {
    if (RequestType.Benchmark === requestType) {
      this.benchmarksRequested = true;
    }

    if (RequestType.HedgeOrder === requestType) {
      this.hedgeOrdersRequested = true;
    }
  }

  checkCleanupSubjects(): void {
    if (this.benchmarksPrimed && this.hedgeOrdersPrimed) {
      this.requestSubject.complete();
      this.responseSubject.complete();
      this.requestSubject = null;
      this.responseSubject = null;
    }
  }

  createRequest(type: new () => T): T {
    let request: T = new type();

    return request;
  }

  primeBenchmark(): Promise<U> {
    if (
      this.benchmarksRequested === true ||
      this.requestSubject == null ||
      this.responseSubject == null
    ) {
      logger.out.error(`${this.constructor.name} a single prime at contruct time is supported`);
      return;
    }

    return new Promise<U>((res, rej) => {
      this.requestSubject.next(RequestType.Benchmark);
      this.responseSubject.subscribe(
        (response) => {
          this.benchmarksPrimed = true;
          res(response);
          this.checkCleanupSubjects();
        },
        (err) => rej(err),
      );
    });
  }

  primeHedgeOrder(): Promise<U> {
    if (
      this.hedgeOrdersRequested === true ||
      this.requestSubject == null ||
      this.responseSubject == null
    ) {
      logger.out.error(`${this.constructor.name} a single prime at contruct time is supported`);
      return;
    }

    return new Promise<U>((res, rej) => {
      this.requestSubject.next(RequestType.HedgeOrder);
      this.responseSubject.subscribe(
        (response) => {
          this.hedgeOrdersPrimed = true;
          res(response);
          this.checkCleanupSubjects();
        },
        (err) => rej(err),
      );
    });
  }

  // TODO de-permed/errored client removal of service
  // skipBenchmarkFixingSummaries(): CubiqTS.StandardReport<CubiqTS.BMTimeCodeImpl> {
  // }

  // TODO de-permed/errored hedge removal of service
  // skipHedgeFixingSummaries(): CubiqTS.StandardReport<CubiqTS.BMTimeCodeImpl> {
  // }
}
